/**
 * FindPayCheckout — reusable drawer used by every Find2Near tab that
 * takes payments (Cab, Hotel, Property, Shopping, etc.).
 *
 * Usage:
 *
 *   const [open, setOpen] = useState(false);
 *   <FindPayCheckout
 *      open={open}
 *      onOpenChange={setOpen}
 *      merchantId="fpm_xxx"
 *      amountPaise={28900}
 *      currency="INR"
 *      description="Cab ride · Marine Drive → Andheri"
 *      onSuccess={(txn) => { proceedToBooking(txn); }}
 *   />
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { QrCode, Smartphone, CreditCard, Landmark, Coins, ShieldCheck, Loader2, CheckCircle2, XCircle, Globe2 } from "lucide-react";

const RAILS = [
  { id: "upi",              label: "UPI (BHIM · GPay · PhonePe)", Icon: Smartphone, blurb: "Merchant MDR ₹0 · Instant" },
  { id: "rupay_debit",      label: "RuPay Debit",                 Icon: CreditCard, blurb: "Merchant MDR ₹0 · Fast" },
  { id: "debit_card",       label: "Debit Card (Visa/MC)",        Icon: CreditCard, blurb: "Small fee" },
  { id: "credit_card",      label: "Credit Card",                 Icon: CreditCard, blurb: "Fee may apply" },
  { id: "netbanking",       label: "Netbanking",                  Icon: Landmark,   blurb: "Flat fee" },
  { id: "cod",              label: "Cash on Delivery",            Icon: Coins,      blurb: "Pay in cash on arrival" },
];

function paiseToStr(paise, ccy = "INR") {
  if (paise == null) return "—";
  const n = Number(paise) / 100;
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: ccy, maximumFractionDigits: 2 }).format(n);
  } catch { return `${ccy} ${n.toFixed(2)}`; }
}

export default function FindPayCheckout({
  open, onOpenChange,
  merchantId,
  amountPaise,
  currency = "INR",
  description = "",
  context = null,
  onSuccess,
}) {
  const [rail, setRail] = useState("upi");
  const [intent, setIntent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);      // 'ok' | 'fail' | null
  const [feePreview, setFeePreview] = useState(null);
  const [display, setDisplay] = useState(null);    // {home_currency, indicative:{amount,currency,note}}
  const mockTimerRef = useRef(null);

  // Browser locale → best-guess home currency for the indicative FX display.
  const homeCcy = useMemo(() => {
    try {
      const loc = (navigator.language || "en-IN").toLowerCase();
      if (loc.endsWith("in")) return "INR";
      if (loc.endsWith("us")) return "USD";
      if (loc.endsWith("gb")) return "GBP";
      if (loc.endsWith("ae")) return "AED";
      if (loc.endsWith("sg")) return "SGD";
      if (loc.endsWith("au")) return "AUD";
      if (loc.endsWith("ca")) return "CAD";
      if (loc.endsWith("jp")) return "JPY";
      if (loc.startsWith("de") || loc.startsWith("fr") || loc.startsWith("it") || loc.startsWith("es")) return "EUR";
      return null;
    } catch { return null; }
  }, []);

  useEffect(() => {
    if (!open) {
      setIntent(null); setStatus(null); setBusy(false);
      if (mockTimerRef.current) { clearTimeout(mockTimerRef.current); mockTimerRef.current = null; }
    }
    return () => { if (mockTimerRef.current) clearTimeout(mockTimerRef.current); };
  }, [open]);

  const start = async () => {
    if (!merchantId) { toast.error("Missing merchant."); return; }
    if (!amountPaise || amountPaise <= 0) { toast.error("Invalid amount."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/findpay/intent", {
        merchant_id: merchantId,
        amount_paise: amountPaise,
        currency, rail, description, context,
        home_currency: homeCcy,
      });
      setIntent(data);
      setFeePreview(data.fee_split);
      setDisplay(data.display || null);
      if (data.provider === "mock") {
        // Auto-complete after 800ms for good UX; user can also cancel via drawer.
        mockTimerRef.current = setTimeout(() => complete(data.provider_intent_id, "SUCCESS"), 800);
      } else if (data.checkout?.upi_intent_url) {
        window.location.href = data.checkout.upi_intent_url;
      } else if (data.checkout?.payment_url) {
        window.location.href = data.checkout.payment_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start payment");
    } finally { setBusy(false); }
  };

  const complete = async (intentId, state = "SUCCESS") => {
    setBusy(true);
    try {
      const { data } = await api.post("/findpay/mock/complete", { intent_id: intentId, state });
      if (data?.txn?.state === "SUCCESS") {
        setStatus("ok");
        toast.success("Payment successful");
        onSuccess?.(data.txn);
      } else {
        setStatus("fail");
        toast.error("Payment did not complete");
      }
    } catch (e) {
      setStatus("fail");
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center" onClick={() => onOpenChange?.(false)} data-testid="findpay-checkout">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 text-white">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest opacity-70">FindPay</div>
            <ShieldCheck size={18} />
          </div>
          <div className="mt-2 text-3xl font-bold" data-testid="findpay-amount">{paiseToStr(amountPaise, currency)}</div>
          <div className="text-xs opacity-80 mt-1">{description || "Payment"}</div>
          {display?.indicative && (
            <div className="mt-2 inline-flex items-center gap-1.5 bg-white/15 rounded-full px-2.5 py-1 text-[11px]" data-testid="findpay-fx-pill">
              <Globe2 size={12} />
              <span>~ {display.indicative.currency} {display.indicative.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
              <span className="opacity-70">· indicative</span>
            </div>
          )}
        </div>

        {!intent && (
          <div className="p-4 space-y-2">
            <div className="text-xs font-semibold text-stone-500 uppercase tracking-widest">Choose how to pay</div>
            {RAILS.map(r => {
              const active = rail === r.id;
              return (
                <button key={r.id} onClick={() => setRail(r.id)} data-testid={`findpay-rail-${r.id}`}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition ${active ? "border-emerald-500 bg-emerald-50" : "border-stone-200 hover:border-stone-300"}`}>
                  <r.Icon size={22} className={active ? "text-emerald-700" : "text-stone-500"} />
                  <div className="flex-1">
                    <div className="font-semibold text-stone-800 text-sm">{r.label}</div>
                    <div className="text-[11px] text-stone-500">{r.blurb}</div>
                  </div>
                  {active && <CheckCircle2 size={18} className="text-emerald-600" />}
                </button>
              );
            })}
            <Button onClick={start} disabled={busy} data-testid="findpay-pay-now" className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 h-12 text-base font-semibold">
              {busy ? <Loader2 size={18} className="animate-spin" /> : `Pay ${paiseToStr(amountPaise, currency)}`}
            </Button>
            <div className="text-[10px] text-stone-400 text-center leading-tight px-3">
              FindPay does not store your card. Payments happen on your bank / UPI provider. RBI-compliant.
            </div>
          </div>
        )}

        {intent && status === null && (
          <div className="p-6 text-center">
            <Loader2 size={40} className="animate-spin mx-auto text-emerald-600" />
            <div className="text-sm text-stone-600 mt-3">Processing your payment…</div>
            <div className="text-[11px] text-stone-400 mt-1">Ref: {intent.provider_intent_id}</div>
            {intent.provider === "mock" && (
              <div className="mt-4 flex gap-2 justify-center">
                <button onClick={() => complete(intent.provider_intent_id, "FAILED")} className="text-xs text-red-500 underline" data-testid="findpay-mock-fail">Simulate failure</button>
              </div>
            )}
          </div>
        )}

        {status === "ok" && (
          <div className="p-6 text-center">
            <CheckCircle2 size={56} className="mx-auto text-emerald-600" />
            <div className="text-lg font-semibold mt-3">Payment successful</div>
            <FeePanel fee={feePreview} currency={currency} />
            <Button onClick={() => onOpenChange?.(false)} className="mt-4 w-full h-11" data-testid="findpay-close">Done</Button>
          </div>
        )}

        {status === "fail" && (
          <div className="p-6 text-center">
            <XCircle size={56} className="mx-auto text-red-500" />
            <div className="text-lg font-semibold mt-3">Payment failed</div>
            <div className="text-xs text-stone-500 mt-1">Nothing was charged. You can try again.</div>
            <Button onClick={() => { setIntent(null); setStatus(null); }} className="mt-4 w-full h-11 bg-emerald-600 hover:bg-emerald-700" data-testid="findpay-retry">Try again</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function FeePanel({ fee, currency }) {
  if (!fee) return null;
  return (
    <div className="mt-4 border rounded-xl p-3 text-left text-xs space-y-1">
      <Row label="You paid"           value={paiseToStr(fee.customer_paid_paise, currency)} bold />
      <Row label="Payment cost"       value={paiseToStr(fee.payment_cost_paise, currency)} muted />
      <Row label="Find2Near subsidy"  value={`- ${paiseToStr(fee.subsidy_paise, currency)}`} muted />
      <Row label="Platform fee"       value={paiseToStr(fee.platform_fee_paise, currency)} muted />
      <div className="border-t my-1"></div>
      <Row label="Merchant receives"  value={paiseToStr(fee.merchant_receives_paise, currency)} bold />
    </div>
  );
}
function Row({ label, value, bold, muted }) {
  return (
    <div className="flex justify-between">
      <span className={muted ? "text-stone-500" : "text-stone-700"}>{label}</span>
      <span className={bold ? "font-semibold text-stone-900" : "text-stone-800"}>{value}</span>
    </div>
  );
}
